/**
 * THE WIRE CONTRACT.
 *
 * Imported by the Durable Object AND by the browser bundle, which is exactly why
 * it lives in its own package: those two are deployed on completely different
 * schedules and can never be upgraded together. The DO Worker deploys in a
 * maintenance window; the browser bundle sits cached in a user's tab for as long
 * as that tab is open. A live call can therefore have a v1 browser talking to a
 * v2 DO, or the reverse.
 *
 * SO: the binary frame carries a VERSION BYTE, and the DO accepts version N and
 * N-1 forever. That path is written now, in M1, not "later" — because the first
 * time it is needed is the first time a deploy happens during a live call, and by
 * then it is too late to add.
 *
 * Two channels, deliberately:
 *   /control  JSON,   main thread,     session lifecycle + tool status + telemetry
 *   /media    binary, dedicated worker, encoded audio chunks
 *
 * They are separate because a 10 KB audio chunk must never head-of-line-block a
 * control event, and because routing binary through the main thread costs two
 * extra structured-clone hops on the UI thread. It also means the two can fail
 * independently, which is better forensics rather than worse: "recording uplink
 * down, call fine" is a real state and it deserves its own name.
 */

// ============================================================================
// Versioning
// ============================================================================

/** Current wire version. Bump ONLY with a matching N-1 branch in the DO. */
export const WIRE_VERSION = 1 as const;

/** Versions the server still accepts. Never shrink this without a deprecation. */
export const ACCEPTED_WIRE_VERSIONS: readonly number[] = [1];

export { RECORDING_DIAGNOSTIC_LEVELS, parseRecordingDiagnostic, recordingErrorName } from './recording-diagnostics.js';
export type { RecordingDiagnostic, RecordingDiagnosticCode } from './recording-diagnostics.js';

// ============================================================================
// Binary audio frame  (browser -> DO, over /media)
// ============================================================================

/**
 * One WebSocket binary message = one chunk of one track.
 *
 * Header is fixed-width little-endian, 40 bytes, followed by the payload.
 *
 *   off  size  field
 *   ---  ----  -----------------------------------------------------------
 *     0     1  version          WIRE_VERSION
 *     1     1  track            TrackId
 *     2     1  payloadKind      PayloadKind
 *     3     1  flags            bit0 = first chunk of track, bit1 = last
 *     4     4  seq              uint32, monotonic per (session, track), from 0
 *     8     4  packetCount      uint32, number of encoded packets in payload
 *    12     8  rtpTimestampFirst uint64 (48 kHz clock) of the first packet
 *    20     8  captureEpochMicros uint64, browser wall clock, first packet
 *    28     4  payloadBytes     uint32
 *    32     4  crc32c           uint32 over the payload only
 *    36     4  reserved         zero
 *
 * WHY A CRC: the DO can truncate a message on its own if something goes wrong in
 * the runtime, and a silently short chunk is worse than a missing one. The CRC is
 * over the payload, so a truncation is detected at ingest rather than at playback
 * three weeks later.
 *
 * WHY rtpTimestamp IS THE PRIMARY TIMELINE: it is the 48 kHz Opus RTP clock
 * (RFC 7587), so `(rtp - rtp0) / 48000` is exact stream-local media seconds,
 * INCLUDING gaps. Wall-clock is carried too, but only to anchor the two tracks
 * against each other and against server time — never to compute duration.
 */
export const FRAME_HEADER_BYTES = 40;

export const TrackId = { User: 1, Assistant: 2 } as const;
export type TrackId = (typeof TrackId)[keyof typeof TrackId];

export const TRACK_NAME: Record<TrackId, 'user' | 'assistant'> = {
  [TrackId.User]: 'user',
  [TrackId.Assistant]: 'assistant',
};

/**
 * How the payload is encoded.
 *
 * `OpusPackets` is the primary path: raw Opus packets straight off WebRTC Encoded
 * Transform, length-prefixed. Bit-exact with what crossed the network, zero
 * encode cost, and every packet independently decodable — so losing a middle
 * chunk costs that chunk and not the file.
 *
 * `Pcm16` is the fallback for a browser without Encoded Transform. 13x the bytes;
 * only ever used with a downsampled 16 kHz mono stream.
 */
export const PayloadKind = { OpusPackets: 1, Pcm16: 2 } as const;
export type PayloadKind = (typeof PayloadKind)[keyof typeof PayloadKind];

export const FrameFlags = { FirstOfTrack: 1 << 0, LastOfTrack: 1 << 1 } as const;

export interface FrameHeader {
  version: number;
  track: TrackId;
  payloadKind: PayloadKind;
  flags: number;
  seq: number;
  packetCount: number;
  rtpTimestampFirst: bigint;
  captureEpochMicros: bigint;
  payloadBytes: number;
  crc32c: number;
}

export function encodeFrame(h: Omit<FrameHeader, 'version' | 'crc32c'>, payload: Uint8Array): ArrayBuffer {
  const buf = new ArrayBuffer(FRAME_HEADER_BYTES + payload.byteLength);
  const v = new DataView(buf);
  v.setUint8(0, WIRE_VERSION);
  v.setUint8(1, h.track);
  v.setUint8(2, h.payloadKind);
  v.setUint8(3, h.flags);
  v.setUint32(4, h.seq, true);
  v.setUint32(8, h.packetCount, true);
  v.setBigUint64(12, h.rtpTimestampFirst, true);
  v.setBigUint64(20, h.captureEpochMicros, true);
  v.setUint32(28, payload.byteLength, true);
  v.setUint32(32, crc32c(payload), true);
  v.setUint32(36, 0, true);
  new Uint8Array(buf, FRAME_HEADER_BYTES).set(payload);
  return buf;
}

export class FrameError extends Error {
  /**
   * Declared and assigned explicitly rather than written as a constructor
   * parameter property. A parameter property is one of the few pieces of
   * TypeScript that has to be COMPILED rather than merely erased, which made
   * this file the one thing stopping the frame codec from being loaded under
   * Node's type stripper (`node --experimental-strip-types`) — i.e. exercised
   * outside a bundler. esbuild never cared; the stripper does. Public shape is
   * unchanged: `code` is still readonly and still set once, in the constructor.
   */
  readonly code: 'short' | 'version' | 'length' | 'crc';

  constructor(message: string, code: 'short' | 'version' | 'length' | 'crc') {
    super(message);
    this.code = code;
    this.name = 'FrameError';
  }
}

export function decodeFrame(buf: ArrayBuffer): { header: FrameHeader; payload: Uint8Array } {
  if (buf.byteLength < FRAME_HEADER_BYTES) {
    throw new FrameError(`frame shorter than header: ${buf.byteLength}`, 'short');
  }
  const v = new DataView(buf);
  const version = v.getUint8(0);
  if (!ACCEPTED_WIRE_VERSIONS.includes(version)) {
    throw new FrameError(`unsupported wire version ${version}`, 'version');
  }
  const payloadBytes = v.getUint32(28, true);
  if (FRAME_HEADER_BYTES + payloadBytes !== buf.byteLength) {
    throw new FrameError(
      `declared ${payloadBytes} payload bytes, message carries ${buf.byteLength - FRAME_HEADER_BYTES}`,
      'length',
    );
  }
  const payload = new Uint8Array(buf, FRAME_HEADER_BYTES, payloadBytes);
  const crc = v.getUint32(32, true);
  const actual = crc32c(payload);
  if (crc !== actual) {
    throw new FrameError(`crc mismatch: declared ${crc}, computed ${actual}`, 'crc');
  }
  return {
    header: {
      version,
      track: v.getUint8(1) as TrackId,
      payloadKind: v.getUint8(2) as PayloadKind,
      flags: v.getUint8(3),
      seq: v.getUint32(4, true),
      packetCount: v.getUint32(8, true),
      rtpTimestampFirst: v.getBigUint64(12, true),
      captureEpochMicros: v.getBigUint64(20, true),
      payloadBytes,
      crc32c: crc,
    },
    payload,
  };
}

/**
 * Opus packets inside a payload are length-prefixed with a uint16, because Opus
 * packets are variable length and self-describing only once you know where each
 * one starts. Reassembly on the server needs the boundaries to write a container.
 */
export function packOpusPackets(packets: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of packets) total += 2 + p.byteLength;
  const out = new Uint8Array(total);
  const v = new DataView(out.buffer);
  let o = 0;
  for (const p of packets) {
    v.setUint16(o, p.byteLength, true);
    o += 2;
    out.set(p, o);
    o += p.byteLength;
  }
  return out;
}

export function unpackOpusPackets(payload: Uint8Array): Uint8Array[] {
  const out: Uint8Array[] = [];
  const v = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  let o = 0;
  while (o + 2 <= payload.byteLength) {
    const len = v.getUint16(o, true);
    o += 2;
    if (o + len > payload.byteLength) break; // truncated tail; caller records a gap
    out.push(payload.subarray(o, o + len));
    o += len;
  }
  return out;
}

/** CRC-32C (Castagnoli). Small table build, computed once per module load. */
const CRC32C_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0x82f63b78 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

export function crc32c(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    c = CRC32C_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

// ============================================================================
// Control channel  (JSON, both directions, over /control)
// ============================================================================

/** Browser -> DO. */
export type ClientMsg =
  | { t: 'rec.diagnostic'; diagnostic: import('./recording-diagnostics.js').RecordingDiagnostic }
  /**
   * What happened to the pre-recorded greeting (see CreateSessionResponse's
   * `greetingClip`). `started`: the clip is playing and the caller's mic is
   * held silent until it ends — the DO tells the model the caller heard it.
   * `failed`: it will not play (could not load, decode, or was not ready in
   * time) — the DO falls back to the spoken greeting. `ended`: playback
   * finished, for the log. The browser never plays a clip after `failed`.
   */
  | { t: 'greeting'; event: 'started' | 'ended' | 'failed'; reason?: string; durationMs?: number }
  /** First message after upgrade. Establishes wire version and resumes recording. */
  | {
      t: 'hello';
      wire: number;
      /** Last chunk seq the browser believes the server has, per track. */
      lastSeq?: Partial<Record<'user' | 'assistant', number>>;
      ua: string;
      /** performance.timeOrigin + performance.now() at send, epoch millis. */
      clientEpochMs: number;
      /**
       * Present only when this hello ends a control outage: how many dials it
       * took to get back, and how long the channel was down, as the BROWSER
       * measured it. This is the durable record's only window into reconnects
       * that failed before reaching the server — an upgrade refused at the edge
       * is a worker log line, not a session event, so without this a recovered
       * outage looks instantaneous and an unrecovered one looks like nothing at
       * all (session fb65ac2f, 2026-08-31). Optional and ignorable: an N-1
       * server never reads it.
       */
      reconnect?: { attempts: number; downMs: number };
    }
  /** Clock sync probe. See ClockPong for the 4-timestamp algorithm. */
  | { t: 'ping'; id: number; t0: number }
  /** The browser wants the model to say something / stop / end. Never trusted
   *  as authority — the DO decides whether to act. */
  | { t: 'intent'; intent: 'interrupt' | 'end_call' | 'mute' | 'unmute' }
  /** Free-text turn from the UI (typed rather than spoken). */
  | { t: 'text'; text: string }
  /** Per-turn client telemetry. Small, batched. */
  | { t: 'telemetry'; turns?: TurnMark[]; stats?: RtcStatsSample[] }
  /** The browser observed a gap in its own capture it could not fill. */
  | { t: 'rec.gap'; track: 'user' | 'assistant'; fromSeq: number; toSeq: number; reason: string }
  /** Recorder finished flushing everything it has. */
  | {
      t: 'rec.done';
      track: 'user' | 'assistant';
      lastSeq: number;
      totalPackets: number;
      /**
       * Milliseconds of NON-SILENT audio the client actually captured.
       *
       * Exists because a length comparison stopped being evidence. The assistant
       * track is captured with a Web Audio tap that records CONTINUOUSLY, silence
       * included, so "the file is as long as the call" is true of a perfect
       * recording and equally true of one that captured nothing but silence. The
       * token oracle measures SPEECH, so without this there is nothing comparable
       * to compare it against.
       *
       * Measured from the PCM on the main thread, before encoding — a real RMS
       * over the actual samples rather than a guess from packet sizes (the encoder
       * is given a bitrate target, so size tracks the encoder's decisions as much
       * as the content).
       *
       * Absent on paths that cannot measure it, and absence must read as "not
       * measured" rather than as zero.
       */
      voicedMs?: number;
      /**
       * How this track was captured, stated by the only component that knows.
       *
       * `wire` = the bytes that crossed the network (a sender-side encoded
       * transform). `rendition` = what this connection played, re-encoded from a
       * Web Audio tap, jitter buffer and packet-loss concealment included.
       * Telephony's third value, `carrier`, is set server-side and never arrives
       * here. See docs/next-tasks.md §4b and §6: a rendition must never be described
       * as wire bytes, and that is only enforceable if the artefact says which it
       * is.
       */
      provenance?: 'wire' | 'rendition';
    };

/** DO -> browser. A deliberately NARROW projection of provider events. */
export type ServerMsg =
  /**
   * The outcome of a greeting-render call (CreateSessionRequest.purpose). Sent
   * once, just before the DO ends the call. `saved`: the clip is stored and
   * named by `sha256`. `failed`: nothing was stored; `reason` says why
   * (`no_speech`, `did_not_match`, `caller_spoke`, `too_long`, `save_failed`)
   * and `heard` is what the agent actually said, when it said anything.
   */
  | { t: 'greeting_render'; status: 'saved' | 'failed'; reason?: string; durationMs?: number; sha256?: string; heard?: string }
  | {
      t: 'ready';
      sessionId: string;
      /** Server epoch millis when this was sent, for clock sync. */
      serverEpochMs: number;
      /** Echoed so the client can detect a version mismatch loudly. */
      wire: number;
      /** Per-track seq the server actually has. Browser drains from +1. */
      ackSeq: Record<'user' | 'assistant', number>;
      recording: boolean;
    }
  | { t: 'pong'; id: number; t0: number; t1: number; t2: number }
  /** Live conversation state for the UI. Sanitised: never carries instructions
   *  or tool schemas. See docs/architecture.md §3. */
  | { t: 'speech'; who: 'user' | 'assistant'; state: 'start' | 'stop' }
  | { t: 'transcript'; who: 'user' | 'assistant'; itemId: string; text: string; final: boolean }
  /** Tool activity, for a spinner. Name and the operator-written display
   *  label/blurb only — NEVER arguments or results (a public embed visitor
   *  holds this channel). The display fields are informational furniture set
   *  on the tool definition for exactly this surface; absent when unset. */
  | {
      t: 'tool';
      callId: string;
      name: string;
      state: 'running' | 'ok' | 'error';
      displayName?: string;
      displayDescription?: string;
    }
  /**
   * A SCREEN CARD from the built-in `show_info` tool: content the MODEL
   * composed for the caller's eyes, emitted by the DO when the model calls the
   * tool. Same trust class as the words the model speaks — this is not a tool's
   * arguments and not a tool's result, which still never ride this channel.
   * `body` is a small markdown subset (bold, italic, bullet/numbered lists,
   * https links), bounded at the executor; a widget with a chat panel renders
   * it, every other widget ignores it. `callId` is the provider's function-call
   * id, for dedupe under replay. Additive in wire v1: an older client's message
   * switch simply has no case for it.
   */
  | { t: 'card'; callId: string; title: string | null; body: string }
  | { t: 'ack'; track: 'user' | 'assistant'; seq: number }
  /**
   * LIVE AUDIO ON/OFF. `on: true`: somebody is listening (or about to take
   * over) — open `liveUrl` with the session's `reconnectToken` (the control
   * token has long expired by then) and send the caller's mic and the agent's
   * voice as live frames (./live.ts). `on: false`: close it. Additive in wire v1: a page without live support ignores it, and the
   * DO tells the listener audio is unavailable when no live socket arrives.
   */
  | { t: 'live'; on: boolean }
  /**
   * A PERSON HAS THE CALL. The DO is ending the AI leg; the provider
   * connection closing after this is expected and must NOT end the call on
   * this page. From now on the person's voice arrives as Human live frames
   * on /ws/live, to be played in place of the agent's. The call ends with a
   * terminal `state` message as usual. `ended` is reserved for hand-back.
   */
  | { t: 'takeover'; state: 'started' | 'ended' }
  | { t: 'state'; state: SessionState; detail?: string }
  | { t: 'error'; code: string; message: string; fatal: boolean };

/**
 * Clock reconciliation, NTP-style over the control socket.
 *
 * t0 client send, t1 server recv, t2 server send, t3 client recv.
 *   offset = ((t1 - t0) + (t2 - t3)) / 2
 *   rtt    = (t3 - t0) - (t2 - t1)
 *
 * Take the sample with the MINIMUM rtt out of a burst, do not average: averaging
 * folds in queueing delay, and the least-delayed sample is the most accurate one.
 *
 * Note for the server side: in a Worker, `Date.now()` does not advance during
 * synchronous execution (Spectre mitigation), so t1 === t2 in practice. That is
 * fine — it just means the server contributes no processing time to the estimate,
 * which is true. Capture it as the FIRST statement in the handler.
 */
export const CLOCK_SYNC_BURST = 8;
export const CLOCK_SYNC_INTERVAL_MS = 10_000;

export function clockOffsetMs(t0: number, t1: number, t2: number, t3: number) {
  return { offsetMs: (t1 - t0 + (t2 - t3)) / 2, rttMs: t3 - t0 - (t2 - t1) };
}

// ============================================================================
// Turn model and telemetry
// ============================================================================

/**
 * One turn, as the browser sees it. The DO holds the provider-side anchors and
 * joins the two by `responseId` where it can, and by RTP timestamp where it
 * cannot — the transform worker sees the first assistant packet before any
 * control-channel relay could arrive, so the browser cannot know `responseId` at
 * playout time. See docs/architecture.md §7.
 */
export interface TurnMark {
  /** Monotonic per session, assigned by the browser. */
  n: number;
  /** Local VAD: user speech start/end, client epoch millis. */
  userSpeechStartMs?: number;
  userSpeechEndMs?: number;
  /** First assistant audio actually handed to the output device. */
  assistantFirstPlayoutMs?: number;
  /** RTP timestamp of that first assistant packet — the reliable join key. */
  assistantFirstRtp?: string;
  /** Interrupted by the user. */
  bargedIn?: boolean;
}

/**
 * The getStats() subset that matters for a voice agent. Deltas, not cumulative
 * totals — the browser differences them so the payload stays small and the server
 * does no arithmetic it can get wrong.
 *
 * Average jitter buffer delay = jitterBufferDelay / jitterBufferEmittedCount.
 * That ratio is the single most useful number here: it is how long the receiver
 * held audio back to smooth the network out, i.e. latency this user's connection
 * caused rather than the model.
 */
export interface RtcStatsSample {
  atMs: number;
  windowMs: number;
  packetsReceived?: number;
  packetsLost?: number;
  jitterMs?: number;
  /** jitterBufferDelay / jitterBufferEmittedCount, already divided. */
  avgJitterBufferMs?: number;
  concealedSamples?: number;
  concealmentEvents?: number;
  roundTripTimeMs?: number;
  audioLevelOut?: number;
  audioLevelIn?: number;
}

// ============================================================================
// Session state — three ORTHOGONAL enums, deliberately not one
// ============================================================================

/**
 * Where the conversation is. Owned by the DO.
 *
 * `LIVE_DEGRADED_*` exist because this architecture has failure modes a relay does
 * not: the media plane is browser<->OpenAI, so our components can fail while the
 * conversation continues. Collapsing those into "live" would hide the thing we
 * built this architecture to be able to see.
 */
export type SessionState =
  | 'CREATED'
  | 'LIVE'
  /** Sideband dropped. Conversation continues; no tools, no transcript capture. */
  | 'LIVE_DEGRADED_NO_SIDEBAND'
  /** Control socket gone. Conversation continues; recording buffers locally. */
  | 'LIVE_DEGRADED_NO_CONTROL'
  /** Media uplink for recording gone. Conversation and control both fine. */
  | 'LIVE_DEGRADED_NO_MEDIA_UPLINK'
  | 'ENDED_NORMAL'
  /**
   * THE CALLER WAS HANDED TO A HUMAN, and the AI session ended because of it.
   *
   * Its own value rather than `ENDED_NORMAL`, because the two answer different
   * questions and only one of them is "did the conversation finish". A transferred
   * session ends mid-conversation ON PURPOSE: the caller was still talking, the
   * carrier leg stayed up, and everything this platform can say about what
   * happened next is one audio file with no transcript, no turns and no cost
   * behind it. Filing that as ENDED_NORMAL would put it next to calls that
   * genuinely concluded, in the one table whose job is to be trustworthy — the
   * same argument `terminalStateFor` already makes for not filing a no-answer SIP
   * leg as normal.
   *
   * `state_detail` carries `transfer:<target name>`, so which desk took it is
   * answerable without a join.
   */
  | 'ENDED_TRANSFERRED'
  /**
   * A PERSON TOOK THE CALL OVER from the panel and the AI leg was ended. Live,
   * not terminal: the caller and the person are still talking, through the
   * Durable Object. `state_detail` carries `takeover:<user id>`.
   */
  | 'TAKEN_OVER'
  /**
   * The call ended while a person held it. Its own value for the same reason
   * as ENDED_TRANSFERRED: the conversation did not finish with the AI, and
   * everything after the takeover has no model turns, tokens or cost behind it.
   */
  | 'ENDED_TAKEN_OVER'
  /** We never saw the end. There is no CLOSE frame on a Realtime socket, so an
   *  intentional hangup and a network fault are indistinguishable until a
   *  re-attach 404s. The jobs sweeper resolves these. */
  | 'ENDED_UNKNOWN'
  | 'FAILED';

/**
 * What we can honestly claim about the RECORDING. Owned by the jobs worker at
 * finalisation.
 *
 * The distinction between PARTIAL and UNVERIFIED is the whole point: PARTIAL
 * means we know something is missing and by how much; UNVERIFIED means we could
 * not measure. They must never collapse into "complete".
 */
export type EvidenceState =
  | 'PENDING'
  /** Both oracles agree within tolerance. */
  | 'COMPLETE'
  /** A measured, quantified gap. */
  | 'PARTIAL'
  /** Oracles unavailable or disagreed — e.g. the sideband dropped, which is
   *  exactly when the recording is also most likely damaged. */
  | 'UNVERIFIED'
  | 'ABSENT';

/** Where the artefact is in the pipeline. Owned by the jobs worker. */
export type RecordingState = 'RECORDING' | 'FINALIZING' | 'READY' | 'FAILED';

/** Completeness tolerance before a recording stops being COMPLETE. */
export const COMPLETENESS_TOLERANCE = 0.02;

// ============================================================================
// HTTP API shapes (browser <-> edge-api)
// ============================================================================

export interface CreateSessionRequest {
  agentId: string;
  /** Opaque, for correlating a demo run. Never trusted. */
  label?: string;
  /**
   * Client-asserted key/value facts (`userID`, `ticket`, …), forwarded to the
   * agent's init webhook and HTTP tools — the customer's own systems, which are
   * the only parties that can judge them. Never trusted here: the server bounds
   * them (≤20 entries, key ≤64, value ≤256, strings only, excess dropped) and
   * the public embed path additionally filters keys against the embed's
   * `forwardParams` allowlist.
   */
  params?: Record<string, string>;
  /**
   * What this browser build can do. `greetingClip: true` says it plays a
   * pre-recorded greeting and reports on it; the server hands out
   * `greetingClip` only when it is set. Without it — a widget page left open
   * since before the feature shipped — the model speaks the greeting, rather
   * than the call waiting on a report the old code will never send.
   */
  supports?: { greetingClip?: boolean };
  /**
   * `greeting_render`: not a conversation. The agent says its saved greeting
   * once, the platform records exactly what the model produced, stores it as
   * the agent's greeting clip, and ends the call. Panel only; the embed path
   * ignores it. The session is labelled GREETING_RENDER_LABEL, runs without
   * the init webhook or the recorder, and never fires the end webhook.
   */
  purpose?: 'greeting_render';
}

/** The session label of a greeting-render call (see CreateSessionRequest.purpose). */
export const GREETING_RENDER_LABEL = 'greeting-render';

export interface CreateSessionResponse {
  sessionId: string;
  /**
   * Short TTL, for bringing the two sockets UP. Used for both /control and
   * /media, and not single-use — a `jti` is minted but nothing burns it, and
   * burning it would break the second socket.
   *
   * (This said "Single-use, ... for the FIRST /control upgrade only" and was
   * wrong on both counts. It mattered: a reader who believed it would conclude
   * reconnection was already solved.)
   */
  controlToken: string;
  /**
   * Long TTL, session-scoped, for RE-opening a socket that dropped.
   *
   * `controlToken` expires in two minutes, which is correct for getting started
   * and useless for recovery: a Wi-Fi handover, a laptop sleep or a session-do
   * deploy at minute six left both clients retrying forever against an expired
   * token, so /control never came back, /media never came back, and the browser
   * could never ship the audio it was still holding. That is the recording
   * recovery path the whole design leans on, so it needs a credential that
   * outlives the first two minutes.
   *
   * Its TTL covers the provider's hard 60-minute session cap and no longer, so
   * it cannot outlive the session it names.
   */
  reconnectToken: string;
  controlUrl: string;
  mediaUrl: string;
  /**
   * Where to open the live-audio socket when the DO sends `{t:'live', on:true}`
   * (see ./live.ts). Authenticated with `reconnectToken`, because the request
   * can come at any point in the call, long after `controlToken` expired.
   * Optional: an older edge-api does not send it, and a page without it simply
   * never offers live audio.
   */
  liveUrl?: string;
  wire: number;
  /** Whether the client should run the recorder at all. */
  recording: boolean;
  /**
   * PRESENT ONLY when this call should open with the agent's pre-recorded
   * greeting: a GPT-Live browser call on an agent in `recorded` greeting mode
   * that has a clip. The browser fetches the clip by `sha256` through its own
   * backend, plays it once the call connects, and reports what happened with a
   * `greeting` client message. Absent means the model speaks the greeting, as
   * before. OPTIONAL so a server older than the field needs no change here.
   */
  greetingClip?: { sha256: string; durationMs: number };
}

/** Metadata of an agent's saved greeting clip. The audio itself is fetched
 *  separately and never rides in an agent read. */
export interface GreetingAudioInfo {
  sha256: string;
  durationMs: number;
  /** The words the clip says: the greeting text it was generated from. */
  transcript: string;
  /**
   * The voice the agent spoke it in. A call plays the clip only while this
   * and `transcript` still match the agent's voice and greeting; otherwise the
   * model speaks the greeting until a new clip is generated. NULL on a clip
   * that was not generated by the agent (an early upload), which never plays.
   */
  voice: string | null;
  createdAt: string;
  createdBy: string | null;
}

/** The agent config a human edits in the control panel. */
export interface AgentConfig {
  id: string;
  name: string;
  instructions: string;
  voice: string;
  model: string;
  /** Which registered built-in tools this agent may call. */
  tools: string[];
  /** Ids of DB-backed HTTP tool definitions this agent may call, in provider
   *  order. Ids only — the definitions (urls, headers) never cross this wire. */
  httpToolIds: string[];
  /**
   * Ids of `transfer_targets` rows this agent may hand a call to, in the order
   * they reach the model as the transfer tool's `destination` enum.
   *
   * THIS LIST IS ALSO THE SWITCH: empty means the agent has no transfer tool at
   * all, so a panel that lets a user clear it is turning the feature off for that
   * agent, not just emptying a dropdown.
   */
  transferTargetIds: string[];
  /** Webhook endpoint id fired before the provider connection, or null. */
  initWebhookId: string | null;
  /** Webhook endpoint id fired after finalisation, or null. */
  endWebhookId: string | null;
  /**
   * Webhook endpoint id fired AS A CALLER IS HANDED TO A HUMAN, or null.
   *
   * Its whole point is timing: it goes out in the second between hanging up the
   * AI and the desk's phone ringing, so the receiving system can have the call on
   * screen before the person says hello. Its response is ignored — it cannot
   * cancel or redirect the transfer.
   */
  transferWebhookId: string | null;
  greeting: string | null;
  /**
   * `spoken`: the model says `greeting`. `recorded`: on a GPT-Live browser call
   * the browser plays `greetingAudio` instead. OPTIONAL for the same
   * deploy-ordering reason as `backendModel`; absent reads as `spoken`.
   */
  greetingMode?: 'spoken' | 'recorded';
  /** The saved clip's metadata, or null when there is none. Optional for the
   *  same reason as `greetingMode`. */
  greetingAudio?: GreetingAudioInfo | null;
  transcribeModel: string;
  vadMode: 'server_vad' | 'semantic_vad';
  /**
   * GPT-LIVE ONLY: the Responses model the voice model delegates reasoning and
   * tool calls to. Null means the platform default (the panel renders which).
   * Meaningless on realtime-family agents and preserved across a family switch
   * rather than cleared — a value that comes back when you switch back is less
   * surprising than one that silently vanished. OPTIONAL for the same
   * deploy-ordering reason as `updatedBy`: a panel bundle newer than edge-api
   * must render rows that do not carry the field yet.
   */
  backendModel?: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Who last saved this agent through the panel — the panel-asserted user
   * email, for display. OPTIONAL, deliberately: rows never edited since the
   * column existed have nothing to claim, and an edit made straight against
   * the API has no panel identity. Absent means "unknown", never "nobody".
   */
  updatedBy?: string | null;
}

export interface SessionSummary {
  id: string;
  agentId: string;
  agentName: string | null;
  transport: 'webrtc' | 'sip';
  state: SessionState;
  evidence: EvidenceState;
  recording: RecordingState;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  turnCount: number;
  toolCallCount: number;
  /** From the provider's own token accounting, not our clock. */
  costUsd: string | null;
  completeness: number | null;
  fromNumber?: string | null;
  /**
   * Cloudflare's geolocation of the request that created the session — where
   * the call was initiated from. Populated on the web path (the browser's
   * request carries it for free); NULL on telephony, where the carrier's
   * `fromNumber` is the origin. Country is ISO 3166-1 alpha-2.
   */
  clientCountry?: string | null;
  clientRegion?: string | null;
  clientCity?: string | null;
}

export type { BackendDelegation, LiveConversationItem, LiveConversation } from './delegations.js';
export * from './live.js';
